import { safeLocalStorage } from "./safeStorage";
import { getPartnerConfigBySource } from "../data/partnerConfigs";
import { captureMessage } from "./sentry";

// Attribution parameter keys
export const ATTRIBUTION_PARAMS = {
  // Custom attribution parameters
  MEDIUM: "m",
  SOURCE: "s",
  CAMPAIGN: "cp",
  MATCH_TYPE: "b",
  AD_SET_ID: "a",
  AD_ID: "i",
  AUDIENCE: "t",
  SOURCE_URL: "su",
  CLICK_ID: "cl",
  // UTM parameters
  UTM_SOURCE: "utm_source",
  UTM_MEDIUM: "utm_medium",
  UTM_CAMPAIGN: "utm_campaign",
  UTM_CONTENT: "utm_content",
  UTM_TERM: "utm_term",
} as const;

// Local storage key for attribution data
export const ATTRIBUTION_STORAGE_KEY = "hm_attribution";

// Type for attribution data
export interface AttributionData {
  // Custom attribution parameters
  medium?: string;
  source?: string;
  campaign?: string;
  matchType?: string;
  adSetId?: string;
  adId?: string;
  audience?: string;
  sourceUrl?: string;
  clickId?: string;
  // UTM parameters
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
}

/**
 * Extract attribution parameters from URL search params
 */
export function getAttributionFromUrl(
  searchParams: URLSearchParams,
): AttributionData {
  return {
    // Custom attribution parameters
    medium: searchParams.get(ATTRIBUTION_PARAMS.MEDIUM) || "affiliates", // Default to 'affiliates'
    source: searchParams.get(ATTRIBUTION_PARAMS.SOURCE) || undefined,
    campaign: searchParams.get(ATTRIBUTION_PARAMS.CAMPAIGN) || undefined,
    matchType: searchParams.get(ATTRIBUTION_PARAMS.MATCH_TYPE) || undefined,
    adSetId: searchParams.get(ATTRIBUTION_PARAMS.AD_SET_ID) || undefined,
    adId: searchParams.get(ATTRIBUTION_PARAMS.AD_ID) || undefined,
    audience: searchParams.get(ATTRIBUTION_PARAMS.AUDIENCE) || undefined,
    sourceUrl: searchParams.get(ATTRIBUTION_PARAMS.SOURCE_URL) || undefined,
    clickId: searchParams.get(ATTRIBUTION_PARAMS.CLICK_ID) || undefined,
    // UTM parameters - try custom params first, then fall back to UTM if not present
    utmSource:
      searchParams.get(ATTRIBUTION_PARAMS.SOURCE) ||
      searchParams.get(ATTRIBUTION_PARAMS.UTM_SOURCE) ||
      undefined,
    utmMedium:
      searchParams.get(ATTRIBUTION_PARAMS.MEDIUM) ||
      searchParams.get(ATTRIBUTION_PARAMS.UTM_MEDIUM) ||
      "affiliates",
    utmCampaign:
      searchParams.get(ATTRIBUTION_PARAMS.CAMPAIGN) ||
      searchParams.get(ATTRIBUTION_PARAMS.UTM_CAMPAIGN) ||
      undefined,
    utmContent: searchParams.get(ATTRIBUTION_PARAMS.UTM_CONTENT) || undefined,
    utmTerm: searchParams.get(ATTRIBUTION_PARAMS.UTM_TERM) || undefined,
  };
}

/**
 * Resolve attribution from the URL, using the durable partner path slug as the
 * primary attribution signal and query params (m, s, cp) as enrichment layered on top.
 *
 * Precedence for the reconstructable fields (query always wins when present):
 *   - source:   ?s=  ->  registered partner source  ->  raw slug
 *   - medium:   ?m=  ->  partner.defaultMedium       ->  "affiliates"
 *   - campaign: ?cp= ->  landing config defaultCampaign -> whatever was on the URL
 *
 * When query params survive we keep them; when they are stripped in transit we
 * reconstruct canonical values from the path slug so we still get partner + campaign
 * level attribution rather than "some partner".
 *
 * An unregistered slug is not an error: we still attribute to the raw slug (the durable
 * signal) and log a warning so the missing registry entry is noticed.
 */
export function resolveAttribution(
  searchParams: URLSearchParams,
  options: { partnerSlug?: string; defaultCampaign?: string } = {},
): AttributionData {
  const attribution = getAttributionFromUrl(searchParams);
  const { partnerSlug, defaultCampaign } = options;

  // No partner path segment -> legacy behavior, unchanged.
  if (!partnerSlug) {
    return attribution;
  }

  const partner = getPartnerConfigBySource(partnerSlug);
  if (!partner) {
    captureMessage(
      `Unregistered partner slug in path: ${partnerSlug}`,
      "warning",
      { partner_slug: partnerSlug },
    );
  }

  const source =
    searchParams.get(ATTRIBUTION_PARAMS.SOURCE) || partner?.source || partnerSlug;
  const medium =
    searchParams.get(ATTRIBUTION_PARAMS.MEDIUM) ||
    partner?.defaultMedium ||
    "affiliates";
  const campaign =
    searchParams.get(ATTRIBUTION_PARAMS.CAMPAIGN) ||
    defaultCampaign ||
    attribution.campaign;

  return {
    ...attribution,
    source,
    medium,
    campaign,
    // Keep UTM mirrors consistent with the reconstructed custom params.
    utmSource: source,
    utmMedium: medium,
    utmCampaign: campaign,
  };
}

/**
 * Save attribution data to local storage
 */
export function saveAttributionData(data: AttributionData): void {
  safeLocalStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(data));
}
